import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Folder, FolderOpen, FileText, Upload, Download, Trash2, Pencil,
  Plus, ChevronRight, ChevronDown, Calendar, User, Search,
  Building2, File, FileSpreadsheet, FileImage, FilePlus, History, RefreshCw
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const TYPES_DOCUMENTS = [
  "Compte-rendus de réunions",
  "Compte-rendus de comité de suivi",
  "Bilans mensuels",
  "Bilans finaux"
];

const getFileIcon = (filename) => {
  const ext = filename?.toLowerCase().split('.').pop();
  switch (ext) {
    case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
    case 'doc':
    case 'docx': return <FileText className="w-5 h-5 text-blue-500" />;
    case 'xls':
    case 'xlsx': return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    case 'png':
    case 'jpg':
    case 'jpeg': return <FileImage className="w-5 h-5 text-purple-500" />;
    default: return <File className="w-5 h-5 text-slate-500" />;
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const DocumentsPartages = ({ userRole = 'admin' }) => {
  const { user } = useAuth();
  const [arborescence, setArborescence] = useState([]);
  const [projets, setProjets] = useState([]);
  const [typesDocuments, setTypesDocuments] = useState(TYPES_DOCUMENTS);
  const [loading, setLoading] = useState(true);
  const [expandedProjets, setExpandedProjets] = useState({});
  const [expandedDossiers, setExpandedDossiers] = useState({});
  const [search, setSearch] = useState('');
  
  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showProjetDialog, setShowProjetDialog] = useState(false);
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [uploadData, setUploadData] = useState({ projet_id: '', type_document: '', dossier_id: '', description: '', remplacer_document_id: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newProjet, setNewProjet] = useState({ nom: '', description: '' });
  const [animateurs, setAnimateurs] = useState([]);
  const [selectedAnimateurs, setSelectedAnimateurs] = useState([]);
  const [versionsData, setVersionsData] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [dossiersForUpload, setDossiersForUpload] = useState([]);

  // Vérifier si l'utilisateur peut modifier/supprimer un document
  const canEditDocument = (doc) => {
    if (userRole === 'admin') return true;
    if (userRole === 'referent') return true;
    // Animateur peut modifier/supprimer ses propres documents
    if (userRole === 'animateur' && doc.uploaded_by === user?.id) return true;
    return false;
  };

  // Mettre à jour les dossiers disponibles quand on change de projet
  useEffect(() => {
    if (uploadData.projet_id && arborescence.length > 0) {
      const projetData = arborescence.find(item => item.projet.id === uploadData.projet_id);
      if (projetData) {
        // Combiner dossiers prédéfinis et personnalisés
        const allDossiers = projetData.dossiers || [];
        setDossiersForUpload(allDossiers);
        
        // Mettre à jour les types de documents (pour rétrocompatibilité)
        const types = allDossiers.map(d => d.nom);
        setTypesDocuments(types.length > 0 ? types : TYPES_DOCUMENTS);
      }
    }
  }, [uploadData.projet_id, arborescence]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [arboRes, projetsRes, animateursRes] = await Promise.all([
        api.get('/documents-partages/arborescence'),
        api.get('/projets'),
        userRole === 'admin' ? api.get('/animateurs') : Promise.resolve({ data: [] })
      ]);
      
      setArborescence(arboRes.data || []);
      setProjets(projetsRes.data || []);
      setAnimateurs(animateursRes.data || []);
      
      // Ouvrir tous les projets par défaut
      const expanded = {};
      (arboRes.data || []).forEach(item => {
        expanded[item.projet.id] = true;
      });
      setExpandedProjets(expanded);
      
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const toggleProjet = (projetId) => {
    setExpandedProjets(prev => ({ ...prev, [projetId]: !prev[projetId] }));
  };

  const toggleDossier = (projetId, dossierNom) => {
    const key = `${projetId}-${dossierNom}`;
    setExpandedDossiers(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 10 Mo');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadData.projet_id || !uploadData.type_document) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        const response = await api.post('/documents-partages', {
          projet_id: uploadData.projet_id,
          type_document: uploadData.type_document,
          dossier_id: uploadData.dossier_id || null,  // ID du dossier personnalisé si applicable
          nom_fichier: selectedFile.name,
          contenu_base64: base64,
          description: uploadData.description,
          remplacer_document_id: uploadData.remplacer_document_id || null
        });
        
        const version = response.data.version || 1;
        const message = version > 1 
          ? `Nouvelle version (v${version}) téléversée avec succès` 
          : 'Document téléversé avec succès';
        toast.success(message);
        setShowUploadDialog(false);
        setSelectedFile(null);
        setUploadData({ projet_id: '', type_document: '', dossier_id: '', description: '', remplacer_document_id: '' });
        loadData();
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const handleViewVersions = async (doc) => {
    setLoadingVersions(true);
    setShowVersionsDialog(true);
    try {
      const response = await api.get(`/documents-partages/${doc.id}/versions`);
      setVersionsData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des versions');
      setShowVersionsDialog(false);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleUploadNewVersion = (doc) => {
    setUploadData({
      projet_id: doc.projet_id,
      type_document: doc.type_document,
      description: '',
      remplacer_document_id: doc.id
    });
    setShowUploadDialog(true);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents-partages/${doc.id}`);
      const { contenu_base64, nom_fichier, mime_type } = response.data;
      
      const byteCharacters = atob(contenu_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mime_type || 'application/octet-stream' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nom_fichier;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      await api.delete(`/documents-partages/${docId}`);
      toast.success('Document supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (doc) => {
    setEditingDocument(doc);
    setEditDescription(doc.description || '');
    setShowEditDialog(true);
  };

  const handleUpdateDescription = async () => {
    if (!editingDocument) return;
    
    try {
      await api.put(`/documents-partages/${editingDocument.id}`, {
        description: editDescription
      });
      toast.success('Description mise à jour');
      setShowEditDialog(false);
      setEditingDocument(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleCreateProjet = async () => {
    if (!newProjet.nom.trim()) {
      toast.error('Le nom du projet est obligatoire');
      return;
    }

    try {
      await api.post('/projets', {
        ...newProjet,
        intervenants_ids: selectedAnimateurs
      });
      
      toast.success('Projet créé avec succès');
      setShowProjetDialog(false);
      setNewProjet({ nom: '', description: '' });
      setSelectedAnimateurs([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const filteredArborescence = search
    ? arborescence.filter(item => 
        item.projet.nom.toLowerCase().includes(search.toLowerCase()) ||
        item.dossiers.some(d => 
          d.documents.some(doc => 
            doc.nom_fichier.toLowerCase().includes(search.toLowerCase())
          )
        )
      )
    : arborescence;

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement des documents...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-3xl shadow-sm border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <Folder className="w-7 h-7 inline mr-2 text-indigo-600" />
                Documents Partagés
              </h2>
              <p className="text-slate-600 mt-1">
                Organisation des documents par projet et type
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-10 rounded-xl w-64 bg-white"
                />
              </div>
              
              {['admin', 'referent', 'animateur'].includes(userRole) && (
                <Button
                  onClick={() => setShowUploadDialog(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 rounded-xl"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Téléverser
                </Button>
              )}
              
              {userRole === 'admin' && (
                <Button
                  onClick={() => setShowProjetDialog(true)}
                  variant="outline"
                  className="rounded-xl border-indigo-300 text-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Projet
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Arborescence */}
      {filteredArborescence.length === 0 ? (
        <Card className="bg-white rounded-3xl shadow-sm">
          <CardContent className="p-12 text-center">
            <Folder className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun projet</h3>
            <p className="text-slate-500 mb-4">
              {userRole === 'admin' 
                ? "Créez votre premier projet pour commencer à organiser vos documents."
                : "Aucun projet n'est accessible pour le moment."}
            </p>
            {userRole === 'admin' && (
              <Button onClick={() => setShowProjetDialog(true)} className="rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Créer un projet
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredArborescence.map(item => (
            <Card key={item.projet.id} className="bg-white rounded-3xl shadow-sm overflow-hidden">
              {/* En-tête du projet */}
              <div 
                className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 cursor-pointer hover:from-slate-100 hover:to-slate-200 transition-colors"
                onClick={() => toggleProjet(item.projet.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedProjets[item.projet.id] ? (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  )}
                  <FolderOpen className="w-6 h-6 text-amber-500" />
                  <div>
                    <h3 className="font-semibold text-slate-900">{item.projet.nom}</h3>
                    {item.projet.description && (
                      <p className="text-sm text-slate-500">{item.projet.description}</p>
                    )}
                  </div>
                </div>
                <Badge className="bg-indigo-100 text-indigo-700">
                  {item.total_documents} document{item.total_documents !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              {/* Dossiers du projet */}
              {expandedProjets[item.projet.id] && (
                <div className="border-t border-slate-100">
                  {item.dossiers.map(dossier => {
                    const dossierKey = dossier.id ? `${item.projet.id}-${dossier.id}` : `${item.projet.id}-${dossier.nom}`;
                    const isExpanded = expandedDossiers[dossierKey];
                    const isCustom = dossier.type === 'custom';
                    
                    return (
                      <div key={dossierKey} className="border-b border-slate-50 last:border-0">
                        {/* En-tête du dossier */}
                        <div 
                          className={`flex items-center justify-between px-6 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${isCustom ? 'bg-blue-50/50' : ''}`}
                          onClick={() => toggleDossier(item.projet.id, dossier.id || dossier.nom)}
                        >
                          <div className="flex items-center gap-3 pl-6">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                            <Folder className={`w-5 h-5 ${isCustom ? 'text-emerald-500' : 'text-blue-500'}`} />
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700">{dossier.nom}</span>
                              {isCustom && (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                  Personnalisé
                                </Badge>
                              )}
                            </div>
                            {dossier.description && (
                              <span className="text-xs text-slate-400 ml-2">- {dossier.description}</span>
                            )}
                          </div>
                          <Badge variant="outline" className="text-slate-500">
                            {dossier.count}
                          </Badge>
                        </div>
                        
                        {/* Documents du dossier */}
                        {isExpanded && dossier.documents.length > 0 && (
                          <div className="bg-slate-50 px-6 py-2">
                            {dossier.documents.filter(d => d.est_version_courante !== false).map(doc => (
                              <div 
                                key={doc.id}
                                className="flex items-center justify-between py-2 px-4 ml-12 bg-white rounded-xl mb-2 hover:shadow-sm transition-shadow"
                              >
                                <div className="flex items-center gap-3">
                                  {getFileIcon(doc.nom_fichier)}
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-slate-800 text-sm">{doc.nom_fichier}</p>
                                      {doc.version > 1 && (
                                        <Badge className="bg-purple-100 text-purple-700 text-xs">
                                          v{doc.version}
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-slate-500">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {doc.uploaded_by_nom || 'Inconnu'}
                                      </span>
                                      <span>{formatFileSize(doc.taille)}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-1">
                                  {/* Bouton historique des versions */}
                                  {doc.version > 1 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); handleViewVersions(doc); }}
                                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg"
                                      title="Historique des versions"
                                    >
                                      <History className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {/* Bouton nouvelle version */}
                                  {['admin', 'referent', 'animateur'].includes(userRole) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); handleUploadNewVersion(doc); }}
                                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg"
                                      title="Nouvelle version"
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                                    title="Télécharger"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  {canEditDocument(doc) && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleEdit(doc); }}
                                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg"
                                        title="Modifier"
                                        data-testid={`edit-doc-${doc.id}`}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                        title="Supprimer"
                                        data-testid={`delete-doc-${doc.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {isExpanded && dossier.documents.length === 0 && (
                          <div className="bg-slate-50 px-6 py-4 ml-12 text-center text-sm text-slate-500">
                            Aucun document dans ce dossier
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Upload */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" />
              {uploadData.remplacer_document_id ? 'Téléverser une Nouvelle Version' : 'Téléverser un Document'}
            </DialogTitle>
            <DialogDescription>
              {uploadData.remplacer_document_id 
                ? 'Le fichier sera ajouté comme nouvelle version. L\'ancienne version restera accessible dans l\'historique.'
                : 'Sélectionnez le projet et le type de document pour un classement automatique.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {uploadData.remplacer_document_id && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-amber-700">
                  Nouvelle version du document existant
                </span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Projet *</Label>
              <Select 
                value={uploadData.projet_id}
                onValueChange={(val) => setUploadData({ ...uploadData, projet_id: val })}
                disabled={!!uploadData.remplacer_document_id}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Sélectionnez un projet" />
                </SelectTrigger>
                <SelectContent>
                  {projets.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Dossier de destination *</Label>
              <Select 
                value={uploadData.type_document}
                onValueChange={(val) => {
                  // Trouver le dossier sélectionné pour stocker aussi son ID si c'est un dossier personnalisé
                  const dossier = dossiersForUpload.find(d => d.nom === val);
                  setUploadData({ 
                    ...uploadData, 
                    type_document: val,
                    dossier_id: dossier?.id || ''
                  });
                }}
                disabled={!!uploadData.remplacer_document_id || !uploadData.projet_id}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={uploadData.projet_id ? "Sélectionnez un dossier" : "Sélectionnez d'abord un projet"} />
                </SelectTrigger>
                <SelectContent>
                  {dossiersForUpload.length === 0 && uploadData.projet_id && (
                    <SelectItem value="none" disabled>Aucun dossier disponible</SelectItem>
                  )}
                  {dossiersForUpload.map(dossier => (
                    <SelectItem key={dossier.id || dossier.nom} value={dossier.nom}>
                      <div className="flex items-center gap-2">
                        <Folder className={`w-4 h-4 ${dossier.type === 'custom' ? 'text-emerald-500' : 'text-blue-500'}`} />
                        <span>{dossier.nom}</span>
                        {dossier.type === 'custom' && (
                          <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Personnalisé</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!uploadData.projet_id && (
                <p className="text-xs text-slate-500">Sélectionnez un projet pour voir les dossiers disponibles</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Fichier *</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-indigo-400 transition-colors">
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2 text-indigo-600">
                      {getFileIcon(selectedFile.name)}
                      <span className="font-medium">{selectedFile.name}</span>
                      <span className="text-slate-500 text-sm">({formatFileSize(selectedFile.size)})</span>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <FilePlus className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p>Cliquez pour sélectionner un fichier</p>
                      <p className="text-xs mt-1">PDF, Word, Excel, Images (max 10 Mo)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                placeholder="Description du document..."
                className="rounded-xl"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading}
              className="bg-indigo-600 hover:bg-indigo-700 rounded-xl"
            >
              {uploading ? 'Téléversement...' : 'Téléverser'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nouveau Projet */}
      <Dialog open={showProjetDialog} onOpenChange={setShowProjetDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Nouveau Projet
            </DialogTitle>
            <DialogDescription>
              Créez un nouveau projet pour organiser vos documents.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom du projet *</Label>
              <Input
                value={newProjet.nom}
                onChange={(e) => setNewProjet({ ...newProjet, nom: e.target.value })}
                placeholder="Ex: Lot 1, Lot 7..."
                className="rounded-xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newProjet.description}
                onChange={(e) => setNewProjet({ ...newProjet, description: e.target.value })}
                placeholder="Description du projet..."
                className="rounded-xl"
              />
            </div>
            
            {animateurs.length > 0 && (
              <div className="space-y-2">
                <Label>Intervenants assignés</Label>
                <div className="border rounded-xl p-3 max-h-40 overflow-y-auto space-y-2">
                  {animateurs.map(anim => (
                    <label key={anim.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded-lg">
                      <input
                        type="checkbox"
                        checked={selectedAnimateurs.includes(anim.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAnimateurs([...selectedAnimateurs, anim.id]);
                          } else {
                            setSelectedAnimateurs(selectedAnimateurs.filter(id => id !== anim.id));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{anim.prenom} {anim.nom}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Les intervenants sélectionnés pourront voir et téléverser des documents dans ce projet.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjetDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              onClick={handleCreateProjet}
              className="bg-indigo-600 hover:bg-indigo-700 rounded-xl"
            >
              Créer le projet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Historique des Versions */}
      <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Historique des Versions
            </DialogTitle>
            <DialogDescription>
              {versionsData?.total_versions || 0} version(s) disponible(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingVersions ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-4 text-slate-500">Chargement des versions...</p>
              </div>
            ) : versionsData?.versions?.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {versionsData.versions.map((version, index) => (
                  <div 
                    key={version.id}
                    className={`flex items-center justify-between p-3 rounded-xl border ${
                      version.est_version_courante 
                        ? 'bg-purple-50 border-purple-200' 
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {getFileIcon(version.nom_fichier)}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800 text-sm">{version.nom_fichier}</p>
                          <Badge className={version.est_version_courante 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-slate-200 text-slate-600'
                          }>
                            v{version.version}
                          </Badge>
                          {version.est_version_courante && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              Actuelle
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          <span>{new Date(version.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}</span>
                          <span>par {version.uploaded_by_nom || 'Inconnu'}</span>
                          <span>{formatFileSize(version.taille)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(version)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Aucune version trouvée
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionsDialog(false)} className="rounded-xl">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Édition Description */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-amber-600" />
              Modifier le document
            </DialogTitle>
            <DialogDescription>
              {editingDocument && `Modifier la description de "${editingDocument.nom_fichier}"`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Ajoutez une description pour ce document..."
                className="rounded-xl min-h-[100px]"
                data-testid="edit-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button onClick={handleUpdateDescription} className="rounded-xl gap-2" data-testid="save-edit-btn">
              <Pencil className="w-4 h-4" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPartages;
